import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { SiteContentEditor } from "../components/SiteContentEditor";
import { AppointmentsTable } from "../components/admin/AppointmentsTable";
import { DatePicker } from "../components/DatePicker";
import { CalendarDays, Users, Mail, Power, FileText, Eye, PenSquare, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [settings, setSettings] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [patients, setPatients] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [blockForm, setBlockForm] = useState({ date: "", type: "both", slot: "all", reason: "" });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetail, setPatientDetail] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [editPatient, setEditPatient] = useState(null);
  const [editForm, setEditForm] = useState({ email: "", phone: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [diagAppt, setDiagAppt] = useState(null);
  const [diagText, setDiagText] = useState("");
  const [diagSaving, setDiagSaving] = useState(false);

  const refresh = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/appointments", { params: { status: statusFilter, ...(dateFilter ? { date: dateFilter } : {}) } }).then(({ data }) => setAppointments(data)).catch(() => {});
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/admin/blocked-slots").then(({ data }) => setBlocked(data)).catch(() => {});
    api.get("/admin/patients").then(({ data }) => setPatients(data)).catch(() => {});
    api.get("/admin/email-logs").then(({ data }) => setEmailLogs(data)).catch(() => {});
  }, [statusFilter, dateFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      toast.success(`Appointment ${status}`);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/appointments/${id}/mark-paid`);
      toast.success("Marked as paid");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleSetting = async (key, value) => {
    try {
      const { data } = await api.patch("/admin/settings", { [key]: value });
      setSettings(data);
      toast.success(`${key === "clinic_enabled" ? "Clinic" : "Online"} bookings ${value ? "enabled" : "paused"}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const addBlock = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blocked-slots", blockForm);
      toast.success("Slot(s) blocked");
      setBlockForm({ date: "", type: "both", slot: "all", reason: "" });
      refresh();
    } catch (err) { toast.error(apiError(err)); }
  };

  const removeBlock = async (id) => {
    try {
      await api.delete(`/admin/blocked-slots/${id}`);
      toast.success("Unblocked");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openPatient = async (p) => {
    setSelectedPatient(p);
    setPatientDetail(null);
    try {
      const { data } = await api.get(`/admin/patients/${p.id}/records`);
      setPatientDetail(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/patients/${selectedPatient.id}/notes`, { note: newNote });
      toast.success("Note added — visible to the patient");
      setNewNote("");
      openPatient(selectedPatient);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (p) => {
    setEditPatient(p);
    setEditForm({ email: p.email, phone: p.phone });
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const { data } = await api.patch(`/admin/patients/${editPatient.id}`, editForm);
      toast.success(`Contact updated — Unique ID is now ${data.unique_id}`);
      setEditPatient(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setEditSaving(false); }
  };

  const openDiagnosis = (a) => {
    setDiagAppt(a);
    setDiagText(a.diagnosis || "");
  };

  const saveDiagnosis = async () => {
    setDiagSaving(true);
    try {
      await api.patch(`/admin/appointments/${diagAppt.id}/diagnosis`, { diagnosis: diagText });
      toast.success("Diagnosis saved");
      setDiagAppt(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setDiagSaving(false); }
  };

  const viewRecord = async (rec) => {
    try {
      const { data } = await api.get(`/records/file/${rec.id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl text-slate-900">Clinic Admin Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Doctor's backend team — appointments, availability & patient records.</p>
        </div>
        <Link to="/admin/recovery-requests" data-testid="admin-recovery-link">
          <Button variant="outline" className="rounded-full border-[#5E8B7E] text-[#4A7065] hover:bg-[#D9E4E1]">
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Account Recovery Requests
          </Button>
        </Link>
        <Link to="/admin/professionals" data-testid="admin-professionals-link">
          <Button variant="outline" className="rounded-full border-[#5E8B7E] text-[#4A7065] hover:bg-[#D9E4E1]">
            <Users className="w-4 h-4 mr-1.5" /> Care Providers
          </Button>
        </Link>
        <Link to="/admin/referrals" data-testid="admin-referrals-link">
          <Button variant="outline" className="rounded-full border-[#5E8B7E] text-[#4A7065] hover:bg-[#D9E4E1]">
            <Mail className="w-4 h-4 mr-1.5" /> Referral Requests
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        {[
          { label: "Total Appointments", value: stats?.total_appointments },
          { label: "Pending Requests", value: stats?.pending },
          { label: "Confirmed Today", value: stats?.confirmed_today },
          { label: "Registered Patients", value: stats?.total_patients },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-[#E5EAE8] p-5">
            <div className="text-3xl font-heading font-semibold text-[#2C557E]">{s.value ?? "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="appointments" className="mt-8">
        <TabsList className="bg-[#F4F7F6] rounded-full p-1 flex-wrap h-auto">
          <TabsTrigger data-testid="admin-tab-appointments" value="appointments" className="rounded-full data-[state=active]:bg-[#2C557E] data-[state=active]:text-white">
            <CalendarDays className="w-4 h-4 mr-1.5" /> Appointments
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-availability" value="availability" className="rounded-full data-[state=active]:bg-[#2C557E] data-[state=active]:text-white">
            <Power className="w-4 h-4 mr-1.5" /> Availability
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-patients" value="patients" className="rounded-full data-[state=active]:bg-[#2C557E] data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-1.5" /> Patients
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-emails" value="emails" className="rounded-full data-[state=active]:bg-[#2C557E] data-[state=active]:text-white">
            <Mail className="w-4 h-4 mr-1.5" /> Email Logs
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-content" value="content" className="rounded-full data-[state=active]:bg-[#2C557E] data-[state=active]:text-white">
            <PenSquare className="w-4 h-4 mr-1.5" /> Site Content
          </TabsTrigger>
        </TabsList>

        {/* APPOINTMENTS */}
        <TabsContent value="appointments" className="mt-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <DatePicker
                testId="admin-date-filter"
                value={dateFilter}
                onChange={setDateFilter}
                placeholder="Pick a date"
                className="w-56"
              />
              {dateFilter && (
                <Button data-testid="admin-date-clear" size="sm" variant="ghost" onClick={() => setDateFilter("")} className="rounded-full text-slate-500">
                  Clear date
                </Button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-status-filter" className="w-44 rounded-xl bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["all", "pending", "confirmed", "completed", "cancelled"].map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500">
              {dateFilter ? `Appointments on ${dateFilter}, sorted by time` : "All appointments (latest first) — pick a date to see a day's schedule"}
            </span>
          </div>
          {appointments.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-[#D9E4E1] text-slate-400 text-sm">No appointments found.</div>
          ) : (
            <AppointmentsTable
              appointments={appointments}
              onUpdateStatus={updateStatus}
              onMarkPaid={markPaid}
              onEditDiagnosis={openDiagnosis}
            />
          )}

          <Dialog open={!!diagAppt} onOpenChange={(o) => !o && setDiagAppt(null)}>
            <DialogContent className="max-w-md rounded-2xl" data-testid="diagnosis-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">Diagnosis — {diagAppt?.patient_name}</DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  {diagAppt?.date} at {diagAppt?.slot} · {diagAppt?.service_name}. Visible to the clinic team only.
                </DialogDescription>
              </DialogHeader>
              {diagAppt?.patient_chief_complaints && (
                <p className="text-xs text-slate-600 bg-[#F4F7F6] rounded-xl p-3">
                  <b>Chief complaints (from patient):</b> {diagAppt.patient_chief_complaints}
                </p>
              )}
              <Textarea
                data-testid="diagnosis-input"
                value={diagText}
                onChange={(e) => setDiagText(e.target.value)}
                placeholder="Enter the diagnosis for this visit…"
                className="rounded-xl"
                rows={4}
              />
              <Button data-testid="diagnosis-save-btn" disabled={diagSaving} onClick={saveDiagnosis} className="w-full rounded-full bg-[#2C557E] hover:bg-[#22436a]">
                {diagSaving ? "Saving…" : "Save Diagnosis"}
              </Button>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* AVAILABILITY */}
        <TabsContent value="availability" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-[#E5EAE8] p-6 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold text-slate-800">In-Clinic Appointments</h3>
                <p className="text-xs text-slate-500 mt-1">Mon–Sat, 5–7 PM · Switch off when the doctor is unavailable</p>
              </div>
              <Switch
                data-testid="admin-clinic-toggle"
                checked={!!settings?.clinic_enabled}
                onCheckedChange={(v) => toggleSetting("clinic_enabled", v)}
              />
            </div>
            <div className="bg-white rounded-xl border border-[#E5EAE8] p-6 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold text-slate-800">Online Consultations</h3>
                <p className="text-xs text-slate-500 mt-1">Daily 9–11 AM & 7–9 PM · ₹100 per 15-min slot</p>
              </div>
              <Switch
                data-testid="admin-online-toggle"
                checked={!!settings?.online_enabled}
                onCheckedChange={(v) => toggleSetting("online_enabled", v)}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-[#E5EAE8] p-6">
            <h3 className="font-heading font-semibold text-slate-800 mb-1">Block specific dates / slots</h3>
            <p className="text-xs text-slate-500 mb-4">e.g., conferences, surgeries, leave. Blocked slots become unavailable to patients instantly.</p>
            <form onSubmit={addBlock} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <DatePicker
                testId="admin-block-date"
                value={blockForm.date}
                onChange={(v) => setBlockForm({ ...blockForm, date: v })}
                placeholder="Block date"
              />
              <Select value={blockForm.type} onValueChange={(v) => setBlockForm({ ...blockForm, type: v })}>
                <SelectTrigger data-testid="admin-block-type" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Both modes</SelectItem>
                  <SelectItem value="clinic">Clinic only</SelectItem>
                  <SelectItem value="online">Online only</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="admin-block-slot" placeholder='Slot (HH:MM) or "all"' value={blockForm.slot} onChange={(e) => setBlockForm({ ...blockForm, slot: e.target.value })} className="rounded-xl" />
              <Input data-testid="admin-block-reason" placeholder="Reason (optional)" value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} className="rounded-xl" />
              <Button data-testid="admin-block-submit" type="submit" className="rounded-full bg-[#2C557E] hover:bg-[#22436a]">Block</Button>
            </form>
            <div className="mt-5 space-y-2">
              {blocked.map((b) => (
                <div key={b.id} className="flex items-center justify-between bg-[#F4F7F6] rounded-xl px-4 py-2.5 text-sm">
                  <span className="text-slate-700">
                    <b>{b.date}</b> · {b.type} · {b.slot === "all" ? "Full day" : b.slot}
                    {b.reason && <span className="text-slate-400"> — {b.reason}</span>}
                  </span>
                  <Button data-testid={`admin-unblock-${b.id}`} size="sm" variant="ghost" onClick={() => removeBlock(b.id)} className="text-red-500 hover:bg-red-50 rounded-full">
                    Remove
                  </Button>
                </div>
              ))}
              {blocked.length === 0 && <p className="text-xs text-slate-400">No blocked dates.</p>}
            </div>
          </div>
        </TabsContent>

        {/* PATIENTS */}
        <TabsContent value="patients" className="mt-6">
          <div className="space-y-2">
            {patients.map((p) => (
              <Dialog key={p.id}>
                <div className="w-full bg-white rounded-xl border border-[#E5EAE8] p-4 flex items-center gap-4 hover:border-[#2C557E]/40 transition-colors">
                  <DialogTrigger asChild>
                    <button
                      data-testid={`admin-patient-${p.id}`}
                      onClick={() => openPatient(p)}
                      className="flex-1 text-left flex items-center gap-4"
                    >
                      <span className="w-10 h-10 rounded-full bg-[#2C557E]/10 text-[#2C557E] font-semibold flex items-center justify-center shrink-0">
                        {p.name?.[0]?.toUpperCase()}
                      </span>
                      <div className="flex-1">
                        <div className="font-medium text-slate-800 text-sm flex flex-wrap items-center gap-2">
                          {p.name}
                          {p.unique_id && <Badge className="bg-[#2C557E]/10 text-[#2C557E] hover:bg-[#2C557E]/10 rounded-full font-mono text-[11px]" data-testid={`admin-patient-uid-${p.id}`}>{p.unique_id}</Badge>}
                          {p.abha_id && <Badge variant="outline" className="rounded-full font-mono text-[11px]">{p.abha_id}</Badge>}
                          {!p.verified && <Badge variant="outline" className="rounded-full text-amber-700">Unverified</Badge>}
                        </div>
                        <div className="text-xs text-slate-500">{p.email} · {p.phone}</div>
                      </div>
                      <Eye className="w-4 h-4 text-slate-400 shrink-0" />
                    </button>
                  </DialogTrigger>
                  <Button
                    data-testid={`admin-edit-patient-${p.id}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(p)}
                    className="rounded-full text-slate-500 hover:text-[#2C557E] shrink-0"
                    title="Edit email / phone"
                  >
                    <PenSquare className="w-4 h-4" />
                  </Button>
                </div>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-heading">{p.name} — Patient File</DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {p.unique_id && <Badge className="bg-[#2C557E]/10 text-[#2C557E] hover:bg-[#2C557E]/10 rounded-full font-mono">{p.unique_id}</Badge>}
                    {p.abha_id && <Badge variant="outline" className="rounded-full font-mono">{p.abha_id}</Badge>}
                    {p.age && <Badge variant="outline" className="rounded-full">Age {p.age}</Badge>}
                    <span className="text-slate-500 self-center">{p.email} · {p.phone}</span>
                  </div>
                  {p.chief_complaints && (
                    <p className="text-xs text-slate-600 bg-[#F4F7F6] rounded-xl p-3" data-testid={`admin-patient-complaints-${p.id}`}>
                      <b>Chief complaints:</b> {p.chief_complaints}
                    </p>
                  )}
                  {!patientDetail ? (
                    <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>
                  ) : (
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium text-slate-800 text-sm mb-2">Appointments ({patientDetail.appointments.length})</h4>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {patientDetail.appointments.map((a) => (
                            <div key={a.id} className="text-xs bg-[#F4F7F6] rounded-lg px-3 py-2 text-slate-600">
                              {a.date} {a.slot} · {a.service_name} · <span className="capitalize">{a.status}</span> · ₹{a.fee}
                            </div>
                          ))}
                          {patientDetail.appointments.length === 0 && <p className="text-xs text-slate-400">None</p>}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-800 text-sm mb-2">Uploaded Documents ({patientDetail.records.length})</h4>
                        <div className="space-y-1.5">
                          {patientDetail.records.map((r) => (
                            <div key={r.id} className="flex items-center justify-between text-xs bg-[#F4F7F6] rounded-lg px-3 py-2">
                              <span className="text-slate-600 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />{r.title}</span>
                              <Button size="sm" variant="ghost" onClick={() => viewRecord(r)} className="h-7 rounded-full text-[#2C557E]" data-testid={`admin-view-record-${r.id}`}>View</Button>
                            </div>
                          ))}
                          {patientDetail.records.length === 0 && <p className="text-xs text-slate-400">None</p>}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-800 text-sm mb-2">Doctor's Notes</h4>
                        <div className="space-y-1.5 mb-3">
                          {patientDetail.notes.map((n) => (
                            <div key={n.id} className="text-xs bg-[#D9E4E1]/40 rounded-lg px-3 py-2 text-slate-600">
                              {n.note} <span className="text-slate-400">· {new Date(n.created_at).toLocaleDateString()}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Textarea data-testid="admin-note-input" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note visible to this patient…" className="rounded-xl text-sm" rows={2} />
                          <Button data-testid="admin-note-submit" onClick={addNote} className="rounded-full bg-[#2C557E] hover:bg-[#22436a] self-end">Add</Button>
                        </div>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            ))}
            {patients.length === 0 && (
              <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-[#D9E4E1] text-slate-400 text-sm">No registered patients yet.</div>
            )}
          </div>

          <Dialog open={!!editPatient} onOpenChange={(o) => !o && setEditPatient(null)}>
            <DialogContent className="max-w-sm rounded-2xl" data-testid="edit-patient-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">Edit contact — {editPatient?.name}</DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Update the patient's registered email or phone number.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Email</label>
                  <Input data-testid="edit-patient-email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Phone</label>
                  <Input data-testid="edit-patient-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="rounded-xl" />
                </div>
                <p className="text-xs text-slate-500 bg-[#F4F7F6] rounded-xl p-3">
                  Changing the phone number also updates the patient's Unique ID (DRPR + new number).
                  The patient can log in with their email or the new Unique ID using the same password.
                </p>
                <Button data-testid="edit-patient-save" disabled={editSaving} onClick={saveEdit} className="w-full rounded-full bg-[#2C557E] hover:bg-[#22436a]">
                  {editSaving ? "Saving…" : "Save Changes"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* EMAIL LOGS */}
        <TabsContent value="emails" className="mt-6 space-y-2">
          <p className="text-xs text-slate-500 mb-3">
            All system emails (OTPs, confirmations, receipts). Currently in <b>demo mode</b> — add a Resend API key to send real emails.
          </p>
          {emailLogs.map((l) => (
            <div key={l.id} className="bg-white rounded-xl border border-[#E5EAE8] p-4" data-testid={`email-log-${l.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium text-slate-800">{l.subject}</span>
                <Badge variant="outline" className="rounded-full">{l.mode}</Badge>
                <span className="text-xs text-slate-400 ml-auto">{new Date(l.created_at).toLocaleString()}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">To: {l.to} · Status: {l.status}</div>
            </div>
          ))}
          {emailLogs.length === 0 && (
            <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-[#D9E4E1] text-slate-400 text-sm">No emails yet.</div>
          )}
        </TabsContent>

        {/* SITE CONTENT */}
        <TabsContent value="content" className="mt-6">
          <SiteContentEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}
